/**
 * MIPS Cost Category Calculator Utilities
 * 
 * Implements the MIPS Cost Category scoring algorithm including:
 * - Benchmark range parsing and decile assignment
 * - Partial points calculation (truncated to 0.01)
 * - Case minimum validation
 * - Category score calculation
 * - Cost driver classification
 */

// ============================================
// TYPES
// ============================================

export interface CostBenchmark {
  measureId: string;
  measureTitle: string;
  groupNationalAvg: number | null;
  individualNationalAvg: number | null;
  avgCostPerEpisode: number | null;
  ranges: { [decile: number]: string };
  comments: string;
  excluded: boolean;
}

export interface CostMeasure {
  measureId: string;
  name: string;
  type: 'Population' | 'Chronic' | 'Acute' | 'Procedural';
  episodes: number;
  patients: number;
  totalCost: number;
  avgCost: number;
  practiceAvgCost: number;      // From Score Submission (preferred for display)
  decileScore: number | null;
  points: number | null;
  nationalAvg: number;
  nationalMedian: number | null; // Calculated from D7/D8 boundary (7.5 pts)
  gapPercent: number;
  gapPercentMedian: number | null; // Gap to national median
  gapToD10: number;
  gapToMedian: number | null;   // Absolute gap to median
  opportunity: number;
  scored: boolean;
  unitLabel: string;
  scoreSource?: 'cms_submission' | 'calculated' | 'cms_submission_only'; // Track where score came from
}

export interface CostSupplemental {
  measureId: string;
  category: string;
  qscGroup: string;
  qscDisplayName?: string;
  qscIsAggregate?: boolean;
  providerAvgCost: number;
  nationalAvgCost: number;
  episodesWithService: number;
  providerPctEpisodes: number;
  nationalPctEpisodes: number;
  costVariance: number;
  utilVariance: number;
  driverType: 'STRENGTH' | 'COST' | 'UTILIZATION' | 'BOTH';
  lowVolume?: boolean;           // <11 episodes with service — too noisy to badge
  opportunity: number;           // Total opportunity (cost + utilization)
  costOpportunity?: number;      // Opportunity from cost per service being high
  utilizationOpportunity?: number; // Opportunity from overutilization
  yourSpend: number;
}

export interface CostPatient {
  mbi: string;
  gender: string;
  dob: string;
  dateOfDeath?: string | null;
  age: number;
  hccRisk: number;
  hccPercentile: number;
  totalCost: number;
  nationalAvg: number;
  excessCost: number;
  measureCount: number;
  measures: string[];
  hasReadmission: boolean;
  hasSNF: boolean;
  serviceBreakdown: Array<{ name: string; cost: number; pct: number }>;
  // New derived fields for strategy matching
  chronicConditionCount?: number;
  edVisitCount?: number;
  uniqueNDCCount?: number;
  totalPartDCost?: number;
  strategies?: string[];  // Array of strategy IDs patient is eligible for
}

export interface CostSummary {
  categoryScore: number;
  categoryWeight: number;
  scoredMeasures: number;
  totalMeasures: number;
  totalOpportunity: number;
  uniquePatients: number;
  totalCost: number;
  topOpportunity: {
    measureId: string;
    category: string;
    amount: number;
  } | null;
}

export interface CostDashboardData {
  performanceYear: number;
  practice: {
    tin: string;
    tinLast4: string;
  };
  summary: CostSummary;
  measures: CostMeasure[];
  supplemental: CostSupplemental[];
  patients: CostPatient[];
}

export interface CostStrategy {
  id: string;
  name: string;
  description: string;
  targetCriteria: string;
  expectedImpact: string;
  candidateCount: number;
  totalOpportunity: number;
  // New fields from strategy matching methodology
  cptCodes?: string[];
  color?: string;
  reductionRate?: number;  // Expected cost reduction rate
}

// ============================================
// CONSTANTS
// ============================================

/**
 * Case minimums by measure type
 * A measure is only scored if it meets the case minimum
 */
export const CASE_MINIMUMS: Record<string, number> = {
  // Population-based
  'TPCC_1': 20,    // 20 beneficiaries
  'MSPB_1': 35,    // 35 episodes
  
  // Chronic Condition (20 episodes)
  'COST_D_1': 20,      // Diabetes
  'COST_HF_1': 20,     // Heart Failure
  'COST_ACOPD_1': 20,  // Asthma/COPD
  'COST_DEP_1': 20,    // Depression
  'COST_LBP_1': 20,    // Low Back Pain
  'COST_RA_1': 20,     // Rheumatoid Arthritis
  'COST_CKD_1': 20,    // Chronic Kidney Disease
  'COST_ESRD_1': 20,   // End Stage Renal Disease
  'COST_KTM_1': 20,    // Kidney Transplant Management
  'COST_PC_1': 20,     // Prostate Cancer
  
  // Acute Inpatient (20 episodes)
  'COST_S_1': 20,      // Sepsis
  'COST_STEMI_1': 20,  // STEMI with PCI
  'COST_COPDE_1': 20,  // Inpatient COPD Exacerbation
  'COST_IHCI_1': 20,   // Intracranial Hemorrhage/Stroke
  'COST_LGH_1': 20,    // Lower GI Hemorrhage (groups only)
  'COST_PRC_1': 20,    // Psychoses
  'COST_RIH_1': 20,    // Respiratory Infection
  'COST_IPPCI_1': 20,  // Inpatient PCI
  
  // Procedural (10 episodes per CMS scoring guide)
  'COST_AKID_1': 10,   // Acute Kidney Injury w/ New Inpatient Dialysis (procedural, min 10)
  'COST_CRR_1': 10,    // Colon/Rectal Resection (procedural, min 10)
  'COST_EDV_1': 20,    // Emergency Medicine (care setting, min 20)
  'COST_SSC_1': 10,    // Colonoscopy
  'COST_LSFDD_1': 10,  // Lumbar Spine Fusion
  'COST_KA_1': 10,     // Knee Arthroplasty
  'COST_PHA_1': 10,    // Hip Arthroplasty
  'COST_IOL_1': 10,    // Cataract Removal
  'COST_FIHR_1': 10,   // Hernia Repair
  'COST_LPMSM_1': 10,  // Lumpectomy/Mastectomy
  'COST_MR_1': 10,     // Melanoma Resection
  'COST_EOPCI_1': 10,  // Elective Outpatient PCI
  'COST_NECABG_1': 10, // Non-Emergent CABG
  'COST_RUSST_1': 10,  // Renal Stone Treatment
  'COST_HAC_1': 10,    // Hemodialysis Access
  'COST_CCLI_1': 10,   // Lower Extremity Revascularization
};

/**
 * Measure type classification
 */
export const MEASURE_TYPES: Record<string, 'Population' | 'Chronic' | 'Acute' | 'Procedural'> = {
  'TPCC_1': 'Population',
  'MSPB_1': 'Population',
  'COST_D_1': 'Chronic',
  'COST_HF_1': 'Chronic',
  'COST_ACOPD_1': 'Chronic',
  'COST_DEP_1': 'Chronic',
  'COST_LBP_1': 'Chronic',
  'COST_RA_1': 'Chronic',
  'COST_CKD_1': 'Chronic',
  'COST_ESRD_1': 'Chronic',
  'COST_KTM_1': 'Chronic',
  'COST_PC_1': 'Chronic',
  'COST_S_1': 'Acute',
  'COST_STEMI_1': 'Acute',
  'COST_COPDE_1': 'Acute',
  'COST_IHCI_1': 'Acute',
  'COST_LGH_1': 'Acute',
  'COST_PRC_1': 'Acute',
  'COST_RIH_1': 'Acute',
  'COST_IPPCI_1': 'Acute',
  'COST_AKID_1': 'Procedural', // CMS classifies AKI dialysis as procedural (case min 10)
  'COST_EDV_1': 'Procedural',
  'COST_CRR_1': 'Procedural',
  'COST_SSC_1': 'Procedural',
  'COST_LSFDD_1': 'Procedural',
  'COST_KA_1': 'Procedural',
  'COST_PHA_1': 'Procedural',
  'COST_IOL_1': 'Procedural',
  'COST_FIHR_1': 'Procedural',
  'COST_LPMSM_1': 'Procedural',
  'COST_MR_1': 'Procedural',
  'COST_EOPCI_1': 'Procedural',
  'COST_NECABG_1': 'Procedural',
  'COST_RUSST_1': 'Procedural',
  'COST_HAC_1': 'Procedural',
  'COST_CCLI_1': 'Procedural',
};

/**
 * Measure display names
 */
export const MEASURE_NAMES: Record<string, string> = {
  'TPCC_1': 'Total Per Capita Cost',
  'MSPB_1': 'MSPB Clinician',
  'COST_D_1': 'Diabetes',
  'COST_HF_1': 'Heart Failure',
  'COST_ACOPD_1': 'Asthma/COPD',
  'COST_DEP_1': 'Depression',
  'COST_LBP_1': 'Low Back Pain',
  'COST_RA_1': 'Rheumatoid Arthritis',
  'COST_CKD_1': 'Chronic Kidney Disease',
  'COST_ESRD_1': 'End Stage Renal Disease',
  'COST_KTM_1': 'Kidney Transplant',
  'COST_PC_1': 'Prostate Cancer',
  'COST_S_1': 'Sepsis',
  'COST_STEMI_1': 'STEMI with PCI',
  'COST_COPDE_1': 'IP COPD Exacerbation',
  'COST_IHCI_1': 'Stroke/TIA',
  'COST_LGH_1': 'Lower GI Hemorrhage',
  'COST_PRC_1': 'Psychoses',
  'COST_RIH_1': 'Respiratory Infection',
  'COST_IPPCI_1': 'Inpatient PCI',
  'COST_AKID_1': 'Acute Kidney Injury',
  'COST_EDV_1': 'Emergency Medicine',
  'COST_CRR_1': 'Colon/Rectal Resection',
  'COST_SSC_1': 'Colonoscopy',
  'COST_LSFDD_1': 'Lumbar Spine Fusion',
  'COST_KA_1': 'Knee Arthroplasty',
  'COST_PHA_1': 'Hip Arthroplasty',
  'COST_IOL_1': 'Cataract Removal',
  'COST_FIHR_1': 'Hernia Repair',
  'COST_LPMSM_1': 'Lumpectomy/Mastectomy',
  'COST_MR_1': 'Melanoma Resection',
  'COST_EOPCI_1': 'Elective Outpatient PCI',
  'COST_NECABG_1': 'Non-Emergent CABG',
  'COST_RUSST_1': 'Renal Stone Treatment',
  'COST_HAC_1': 'Hemodialysis Access',
  'COST_CCLI_1': 'Lower Extremity Revasc',
};

/**
 * Care management strategies with evidence-based impact
 */
export const STRATEGIES: CostStrategy[] = [
  {
    id: 'ccm',
    name: 'Chronic Care Management',
    description: 'Non-face-to-face care coordination for patients with 2+ chronic conditions',
    targetCriteria: 'Patients appearing in 2+ cost measures',
    expectedImpact: '$74-$4,000/patient annually, 22% total cost reduction',
    candidateCount: 0,
    totalOpportunity: 0,
  },
  {
    id: 'toc',
    name: 'Transitional Care',
    description: 'Post-discharge follow-up, medication reconciliation, care coordination',
    targetCriteria: 'Patients with readmission flag',
    expectedImpact: '20-50% readmission reduction, $2,311 annual savings per patient',
    candidateCount: 0,
    totalOpportunity: 0,
  },
  {
    id: 'rpm',
    name: 'Remote Patient Monitoring',
    description: 'Home telemonitoring with weight scales and BP cuffs for Heart Failure patients',
    targetCriteria: 'Heart Failure patients (COST_HF_1)',
    expectedImpact: '52% cost reduction, 44% lower 30-day readmission, 3.3:1 ROI',
    candidateCount: 0,
    totalOpportunity: 0,
  },
  {
    id: 'snf',
    name: 'SNF Optimization',
    description: 'Shift SNF patients to home health when appropriate, preferred SNF network',
    targetCriteria: 'Patients with SNF utilization',
    expectedImpact: '3-5% episode savings, 25.9% reduction in IRF payments',
    candidateCount: 0,
    totalOpportunity: 0,
  },
  {
    id: 'ed',
    name: 'ED Diversion',
    description: 'Community paramedicine, 24/7 nurse triage, same-day appointments',
    targetCriteria: 'Emergency Medicine patients (COST_EDV_1)',
    expectedImpact: '38-62% ED reduction',
    candidateCount: 0,
    totalOpportunity: 0,
  },
  {
    id: 'medopt',
    name: 'Medication Optimization',
    description: 'Adherence programs, generic substitution, deprescribing for Diabetes patients',
    targetCriteria: 'Diabetes patients (COST_D_1)',
    expectedImpact: '$2,650-$4,552 per patient savings',
    candidateCount: 0,
    totalOpportunity: 0,
  },
];

// ============================================
// BENCHMARK PARSING
// ============================================

/**
 * Parse benchmark range string into [high, low] values
 * Format: "8456.74 - 8117.31" or "<= 2686.23"
 */
export function parseDecileRange(rangeString: string): [number, number] {
  if (!rangeString || rangeString === '--') {
    return [0, 0];
  }
  
  // Handle Decile 10 format: "<= 2686.23"
  if (rangeString.startsWith('<=') || rangeString.startsWith('≤')) {
    // "+" is load-bearing: "<=" is two chars — a single-char class left "= 766.69"
    // behind and parseFloat'd to NaN, making decile 10 unreachable
    const threshold = parseFloat(rangeString.replace(/^[<=≤]+\s*/, '').trim());
    return [threshold, threshold];
  }
  
  // Handle Deciles 1-9 format: "8456.74 - 8117.31"
  const parts = rangeString.split(' - ');
  if (parts.length === 2) {
    return [parseFloat(parts[0]), parseFloat(parts[1])];
  }
  
  return [0, 0];
}

/**
 * Parse benchmark JSON data into usable format
 */
export function parseBenchmarks(benchmarkData: any[]): Map<string, CostBenchmark> {
  const benchmarks = new Map<string, CostBenchmark>();
  
  for (const row of benchmarkData) {
    const measureId = row['Measure ID'];
    if (!measureId) continue;
    
    const excluded = row['Benchmark Range 1'] === '--' || 
                    row[' Benchmark Range 1'] === '--' ||
                    row['Group National Standardized Average'] === '--';
    
    const ranges: { [decile: number]: string } = {};
    for (let i = 1; i <= 10; i++) {
      // Handle both with and without leading space
      ranges[i] = row[` Benchmark Range ${i}`] || row[`Benchmark Range ${i}`] || '--';
    }
    
    benchmarks.set(measureId, {
      measureId,
      measureTitle: row['Measure Title'] || '',
      groupNationalAvg: parseNumericOrNull(row['Group National Standardized Average']),
      individualNationalAvg: parseNumericOrNull(row['Individual National Standardized Average']),
      avgCostPerEpisode: parseNumericOrNull(row['Average Cost Per Episode or Beneficiary']),
      ranges,
      comments: row['Comments '] || row['Comments'] || '',
      excluded,
    });
  }
  
  return benchmarks;
}

function parseNumericOrNull(value: any): number | null {
  if (value === null || value === undefined || value === '' || value === '--') {
    return null;
  }
  const num = parseFloat(value);
  return isNaN(num) ? null : num;
}

// ============================================
// DECILE SCORING
// ============================================

/**
 * Find the decile (1-10) for a given performance value
 * For cost measures, LOWER cost = HIGHER score (better)
 */
export function findDecile(performanceValue: number, ranges: { [decile: number]: string }): number {
  // Start from best (10) and work down to worst (1)
  for (let decile = 10; decile >= 1; decile--) {
    const [rangeHigh, rangeLow] = parseDecileRange(ranges[decile]);
    
    if (rangeHigh === 0 && rangeLow === 0) continue; // Skip invalid ranges
    
    if (decile === 10) {
      // Decile 10: cost at or below the threshold (best performance)
      if (performanceValue <= rangeLow) return 10;
    } else {
      // Deciles 1-9: inclusive on BOTH ends — published bands have 1-cent
      // gaps, so a value exactly on a band's low boundary belongs to it
      // (a strict > dropped boundary-exact values to decile 1)
      if (performanceValue <= rangeHigh && performanceValue >= rangeLow) {
        return decile;
      }
    }
  }
  
  // Default to worst decile if above all ranges
  return 1;
}

/**
 * Calculate partial points within a decile
 * Formula: Decile + (b - q) / (b - a), truncated to hundredths
 * 
 * Where:
 * - q = Performance Value (cost)
 * - a = Bottom of range (lower cost = better)
 * - b = Top of range (higher cost = worse)
 * 
 * Cost measures are inverse: LOWER cost earns MORE partial points, so the
 * fraction is measured from the expensive (b) end. CMS example: $1,104 in
 * the 6-6.9 band ($1,105.07-$1,037.40) scores ~6.01, not 6.98.
 */
export function calculatePartialPoints(
  performanceValue: number, 
  decile: number, 
  ranges: { [decile: number]: string }
): number {
  if (decile === 10) {
    return 10.00;
  }
  
  const [rangeHigh, rangeLow] = parseDecileRange(ranges[decile]);
  
  if (rangeHigh === rangeLow || rangeHigh === 0) {
    return decile;
  }
  
  // Partial credit grows as cost approaches the band's cheap (low) end
  const partial = Math.max((rangeHigh - performanceValue) / (rangeHigh - rangeLow), 0);
  
  // TRUNCATE to hundredths (not round)
  const points = decile + Math.floor(partial * 100) / 100;
  
  return Math.min(points, decile + 0.99); // Cap at decile + 0.99
}

/**
 * Check if a measure meets its case minimum
 */
export function meetsCaseMinimum(measureId: string, episodeCount: number): boolean {
  const minimum = CASE_MINIMUMS[measureId] ?? 20;
  return episodeCount >= minimum;
}

// ============================================
// CATEGORY SCORE CALCULATION
// ============================================

export interface MeasureScore {
  measureId: string;
  points: number;
  decile: number;
  meetsMinimum: boolean;
}

export interface CategoryScoreResult {
  measures: MeasureScore[];
  rawScore: number;          // As percentage (e.g., 68.43)
  weightedScore: number;     // Points toward final score (e.g., 20.53)
  totalPoints: number;
  totalAvailable: number;
  scoredCount: number;
}

/**
 * Calculate the complete Cost Category score
 */
export function calculateCostCategoryScore(
  measures: Array<{ measureId: string; avgCost: number; episodeCount: number }>,
  benchmarks: Map<string, CostBenchmark>,
  categoryWeight: number = 30
): CategoryScoreResult {
  const scoredMeasures: MeasureScore[] = [];
  
  for (const measure of measures) {
    const meetsMinimum = meetsCaseMinimum(measure.measureId, measure.episodeCount);
    
    if (!meetsMinimum) {
      scoredMeasures.push({
        measureId: measure.measureId,
        points: 0,
        decile: 0,
        meetsMinimum: false,
      });
      continue;
    }
    
    const benchmark = benchmarks.get(measure.measureId);
    if (!benchmark || benchmark.excluded) {
      scoredMeasures.push({
        measureId: measure.measureId,
        points: 0,
        decile: 0,
        meetsMinimum: true,
      });
      continue;
    }
    
    const decile = findDecile(measure.avgCost, benchmark.ranges);
    const points = calculatePartialPoints(measure.avgCost, decile, benchmark.ranges);
    
    scoredMeasures.push({
      measureId: measure.measureId,
      points,
      decile,
      meetsMinimum: true,
    });
  }
  
  // Calculate raw score from scored measures only
  const validMeasures = scoredMeasures.filter(m => m.meetsMinimum && m.points > 0);
  const totalPoints = validMeasures.reduce((sum, m) => sum + m.points, 0);
  const totalAvailable = validMeasures.length * 10;
  
  const rawScore = totalAvailable > 0 ? (totalPoints / totalAvailable) * 100 : 0;
  const weightedScore = (rawScore / 100) * categoryWeight;
  
  return {
    measures: scoredMeasures,
    rawScore,
    weightedScore,
    totalPoints,
    totalAvailable,
    scoredCount: validMeasures.length,
  };
}

// ============================================
// COST DRIVER CLASSIFICATION
// ============================================

export type DriverType = 'STRENGTH' | 'COST' | 'UTILIZATION' | 'BOTH';

/**
 * Classify cost driver type based on cost and utilization comparison.
 * Mirrors the backend classifier (costDashboard.js) — keep thresholds in lockstep.
 * Materiality thresholds prevent noise (e.g. 100.00% vs 99.99%) flipping badges.
 */
const DRIVER_COST_THRESHOLD = 0.10; // provider avg must exceed national by >=10%
const DRIVER_UTIL_THRESHOLD_PP = 2; // provider pct must exceed national by >=2 percentage points

export function classifyDriverType(
  providerAvgCost: number,
  nationalAvgCost: number,
  providerPctEpisodes: number,
  nationalPctEpisodes: number
): DriverType {
  const costIssue = nationalAvgCost > 0 && providerAvgCost > nationalAvgCost * (1 + DRIVER_COST_THRESHOLD);
  const utilIssue = providerPctEpisodes > nationalPctEpisodes + DRIVER_UTIL_THRESHOLD_PP;
  
  if (costIssue && utilIssue) return 'BOTH';
  if (costIssue) return 'COST';
  if (utilIssue) return 'UTILIZATION';
  return 'STRENGTH';
}

/**
 * Calculate opportunity from supplemental data
 */
export function calculateCategoryOpportunity(
  providerAvgCost: number,
  nationalAvgCost: number,
  episodesWithService: number
): number {
  const gap = providerAvgCost - nationalAvgCost;
  return gap > 0 ? gap * episodesWithService : 0;
}

// ============================================
// PATIENT ANALYSIS
// ============================================

/**
 * Calculate age from date of birth
 */
export function calculateAge(dob: string): number {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
}

/**
 * Calculate patient cost multiplier vs national average
 */
export function calculateMultiplier(patientCost: number, nationalAvg: number): string {
  if (nationalAvg === 0) return '—';
  return (patientCost / nationalAvg).toFixed(1);
}

/**
 * Get applicable strategies for a patient
 */
export function getPatientStrategies(patient: CostPatient): Array<{ id: string; name: string }> {
  const strategies: Array<{ id: string; name: string }> = [];
  
  // CCM: Multiple chronic conditions (2+ measures)
  if (patient.measureCount >= 2) {
    strategies.push({ id: 'ccm', name: 'CCM' });
  }
  
  // Transitions of Care: Has readmission
  if (patient.hasReadmission) {
    strategies.push({ id: 'toc', name: 'Transitions' });
  }
  
  // RPM: Heart Failure patients
  if (patient.measures.includes('COST_HF_1')) {
    strategies.push({ id: 'rpm', name: 'RPM' });
  }
  
  // SNF Optimization: Has SNF utilization
  if (patient.hasSNF) {
    strategies.push({ id: 'snf', name: 'SNF Opt' });
  }
  
  // ED Diversion: Emergency Medicine measure
  if (patient.measures.includes('COST_EDV_1')) {
    strategies.push({ id: 'ed', name: 'ED Diversion' });
  }
  
  // Medication Optimization: Diabetes patients
  if (patient.measures.includes('COST_D_1')) {
    strategies.push({ id: 'medopt', name: 'Med Opt' });
  }
  
  return strategies;
}

// ============================================
// FORMATTING UTILITIES
// ============================================

/**
 * Format currency value
 */
export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'N/A';
  if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `$${Math.round(value / 1000)}K`;
  return `$${Math.round(value).toLocaleString()}`;
}

/**
 * Format currency with full precision
 */
export function formatCurrencyFull(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'N/A';
  return `$${Math.round(value).toLocaleString()}`;
}

/**
 * Format percentage
 */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'N/A';
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
}

/**
 * Format date
 */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

/**
 * Get color for decile score
 */
export function getDecileColor(score: number | null): string {
  if (score === null) return '#9CA3AF';  // Gray - not scored
  if (score >= 8) return '#10B981';       // Green - excellent
  if (score >= 6) return '#F59E0B';       // Amber - moderate
  if (score >= 4) return '#F97316';       // Orange - needs improvement
  return '#EF4444';                        // Red - poor
}

/**
 * Get color for measure type
 */
export function getMeasureTypeColor(type: string): string {
  const colors: Record<string, string> = {
    'Population': '#3B82F6',
    'Chronic': '#8B5CF6',
    'Acute': '#EF4444',
    'Procedural': '#10B981',
  };
  return colors[type] || '#6B7280';
}

/**
 * Get color for cost tier
 */
export function getCostTierColor(cost: number): string {
  if (cost < 40000) return '#10B981';  // Green - Normal
  if (cost < 80000) return '#F59E0B';  // Amber - Elevated
  return '#EF4444';                     // Red - Outlier
}

// ============================================
// API DATA FETCHING
// ============================================

const getApiUrl = () => {
  // @ts-ignore
  return import.meta.env.VITE_API_URL || 'http://localhost:3001';
};

/**
 * Fetch cost dashboard data from API
 */
export async function getCostDashboardData(
  practiceId: string,
  options?: { npi?: string; year?: number }
): Promise<CostDashboardData | null> {
  try {
    const apiUrl = getApiUrl();
    const token = localStorage.getItem('auth_token');

    // year here is the PERFORMANCE year; the backend resolves it to the
    // cost-data year (P-1, with fallback) via getCostYearForPerformanceYear.
    const params = new URLSearchParams();
    if (options?.npi) params.append('npi', options.npi);
    if (options?.year) params.append('year', String(options.year));
    const qs = params.toString();
    let url = `${apiUrl}/api/practices/${practiceId}/cost/dashboard${qs ? `?${qs}` : ''}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      console.error('Failed to fetch cost dashboard data:', response.status);
      return null;
    }
    
    return response.json();
  } catch (error) {
    console.error('Error fetching cost dashboard data:', error);
    return null;
  }
}

/**
 * Fetch cost benchmarks
 */
export async function getCostBenchmarks(year: number = 2025): Promise<Map<string, CostBenchmark>> {
  try {
    const apiUrl = getApiUrl();
    const token = localStorage.getItem('auth_token');
    
    const response = await fetch(`${apiUrl}/api/cost-benchmarks?year=${year}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      console.error('Failed to fetch benchmarks:', response.status);
      return new Map();
    }
    
    const data = await response.json();
    return parseBenchmarks(data);
  } catch (error) {
    console.error('Error fetching benchmarks:', error);
    return new Map();
  }
}

/**
 * Save cost score to database
 */
export async function saveCostScore(
  practiceId: string,
  scoreData: {
    rawScore: number;
    weightedScore: number;
    npi?: string;
  }
): Promise<boolean> {
  try {
    const apiUrl = getApiUrl();
    const token = localStorage.getItem('auth_token');
    
    const response = await fetch(`${apiUrl}/api/practices/${practiceId}/cost/score`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(scoreData),
    });
    
    return response.ok;
  } catch (error) {
    console.error('Error saving cost score:', error);
    return false;
  }
}

